/**
 * La cartera de cheques.
 *
 * UN CHEQUE ES PLATA CON FECHA
 * ----------------------------
 * Por eso la fila no muestra solo el importe: muestra cuantos dias faltan para
 * cobrarlo, y si ese numero ya se paso, el cheque se marca. Un cheque vencido
 * todavia en cartera es plata que nadie fue a buscar.
 *
 * Y como los cheques pueden ser en pesos o en dolares, cada fila declara su
 * moneda con simbolo, chip y color. Los totales van separados por moneda: una
 * cartera "de 850 mil" no significa nada si adentro hay dolares.
 */
import { Ban, CircleCheck, Landmark, Send, TriangleAlert } from "lucide-react";

import { Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { Kpi } from "@/components/ui/kpi";
import { SkeletonTabla, SkeletonTarjetas } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { cn, formatearFecha } from "@/lib/utils";
import { aNumero } from "@/lib/tipos";

import {
  etiquetaEstadoCheque,
  firmanteCheque,
  motivoRechazo,
  sePuedeAcreditar,
  sePuedeDepositar,
  sePuedeEndosar,
  sePuedeRechazar,
  tonoEstadoCheque,
  totalizarPorMoneda,
  urgenciaCheque,
  type ChequeCartera,
  type Urgencia,
} from "./logica";

const COLUMNAS = 6;

/** Como se dice, en castellano, cuantos dias faltan. */
export function textoVencimiento(urgencia: Urgencia, dias: number | null): string {
  if (urgencia === "SIN_FECHA" || dias === null) return "Sin fecha de cobro";
  if (dias < 0) return `Venció hace ${Math.abs(dias)} ${Math.abs(dias) === 1 ? "día" : "días"}`;
  if (dias === 0) return "Se cobra hoy";
  if (dias === 1) return "Se cobra mañana";
  return `Faltan ${dias} días`;
}

export interface ListaChequesProps {
  cheques: ReadonlyArray<ChequeCartera>;
  hoy?: Date;
  onDepositar: (cheque: ChequeCartera) => void;
  onRechazar: (cheque: ChequeCartera) => void;
  /** Endosar a un proveedor (0155). Sin esto no se ofrece. */
  onEndosar?: (cheque: ChequeCartera) => void;
  /** «Se acreditó» (0155). Sin esto no se ofrece. */
  onAcreditar?: (cheque: ChequeCartera) => void;
  mensajeVacio?: string;
  /** Mientras se traen: skeleton en la primera carga, lista atenuada despues; nunca el mensaje vacio. */
  cargando?: boolean;
}

export function ListaCheques({
  cheques,
  hoy = new Date(),
  onDepositar,
  onRechazar,
  onEndosar,
  onAcreditar,
  mensajeVacio = "No hay cheques con este filtro.",
  cargando = false,
}: ListaChequesProps): JSX.Element {
  const totales = totalizarPorMoneda(cheques);
  const vacio = cheques.length === 0;
  // Skeleton solo en la primera carga (06 §4.2); con datos en pantalla, un
  // cambio de filtro atenua la lista y nunca la vacia.
  const primeraCarga = cargando && vacio;
  const actualizando = cargando && !vacio;

  return (
    <div
      aria-busy={actualizando || undefined}
      className={cn("space-y-3 transition-opacity duration-150", actualizando && "opacity-60")}
    >
      {/* Los totales llevan ademas la cantidad, que no sale de sumar la columna. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Kpi
          etiqueta={`${totales.cantidadArs} ${totales.cantidadArs === 1 ? "cheque" : "cheques"} en pesos`}
          monto={totales.ars}
          moneda="ARS"
          tamano="md"
        />
        <Kpi
          etiqueta={`${totales.cantidadUsd} ${totales.cantidadUsd === 1 ? "cheque" : "cheques"} en dólares`}
          monto={totales.usd}
          moneda="USD"
          tamano="md"
        />
      </div>

      {/* Desktop: tabla densa. */}
      <Card padding="none" className="hidden overflow-hidden md:block">
        {primeraCarga ? (
          <SkeletonTabla columnas={COLUMNAS} />
        ) : (
          <Table densidad="normal" alto="acotado">
            <TableHeader>
              <TableRow>
                <TableHead>Cheque</TableHead>
                <TableHead>Librador</TableHead>
                <TableHead>Se cobra</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Importe</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vacio ? (
                <TableVacia colSpan={COLUMNAS} mensaje={mensajeVacio} />
              ) : (
                cheques.map((ch) => {
                  const { urgencia, dias } = urgenciaCheque(ch, hoy);
                  return (
                    <TableRow key={ch.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        <Codigo className="font-medium">{ch.numero}</Codigo>
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {ch.tipo === "ECHEQ" ? "e-cheq" : "papel"} · {ch.banco ?? "sin banco"}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[14rem] truncate text-sm">
                        {firmanteCheque(ch)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Vencimiento cheque={ch} urgencia={urgencia} dias={dias} />
                      </TableCell>
                      <TableCell>
                        <EstadoCheque estado={ch.estado} />
                      </TableCell>
                      <TableCellImporte
                        monto={aNumero(ch.importe)}
                        moneda={ch.moneda}
                        tamano="sm"
                      />
                      <TableCell className="text-right">
                        <Acciones
                          cheque={ch}
                          onDepositar={onDepositar}
                          onRechazar={onRechazar}
                          onEndosar={onEndosar}
                          onAcreditar={onAcreditar}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Mobile: una tarjeta-registro por cheque, con la barra lateral de su moneda. */}
      <div className="flex flex-col gap-2 md:hidden">
        {primeraCarga ? (
          <SkeletonTarjetas />
        ) : vacio ? (
          <EstadoVacio titulo={mensajeVacio} />
        ) : (
          cheques.map((ch) => {
            const { urgencia, dias } = urgenciaCheque(ch, hoy);
            return (
              <Card
                key={ch.id}
                as="article"
                padding="compacto"
                acento={ch.moneda}
                className="space-y-2"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      <Codigo>{ch.numero}</Codigo> · {ch.banco ?? "sin banco"}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{firmanteCheque(ch)}</p>
                  </div>
                  <Importe monto={aNumero(ch.importe)} moneda={ch.moneda} tamano="lg" />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <EstadoCheque estado={ch.estado} />
                  <Vencimiento cheque={ch} urgencia={urgencia} dias={dias} />
                </div>

                <Acciones
                  cheque={ch}
                  onDepositar={onDepositar}
                  onRechazar={onRechazar}
                  onEndosar={onEndosar}
                  onAcreditar={onAcreditar}
                />
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

function EstadoCheque({ estado }: { estado: string }): JSX.Element {
  return <Badge variant={tonoEstadoCheque(estado)}>{etiquetaEstadoCheque(estado)}</Badge>;
}

function Acciones({
  cheque,
  onDepositar,
  onRechazar,
  onEndosar,
  onAcreditar,
}: {
  cheque: ChequeCartera;
  onDepositar: (c: ChequeCartera) => void;
  onRechazar: (c: ChequeCartera) => void;
  onEndosar?: (c: ChequeCartera) => void;
  onAcreditar?: (c: ChequeCartera) => void;
}): JSX.Element | null {
  const depositable = sePuedeDepositar(cheque.estado);
  const rechazable = sePuedeRechazar(cheque.estado);
  const endosable = onEndosar !== undefined && sePuedeEndosar(cheque.estado);
  const acreditable = onAcreditar !== undefined && sePuedeAcreditar(cheque.estado);
  if (!depositable && !rechazable && !endosable && !acreditable) return null;

  return (
    <div className="flex flex-wrap justify-end gap-2">
      {acreditable ? (
        <Button type="button" size="sm" variant="outline" onClick={() => onAcreditar(cheque)}>
          <CircleCheck aria-hidden="true" />
          Se acreditó
        </Button>
      ) : null}
      {endosable ? (
        <Button type="button" size="sm" variant="outline" onClick={() => onEndosar(cheque)}>
          <Send aria-hidden="true" />
          Endosar
        </Button>
      ) : null}
      {depositable ? (
        <Button type="button" size="sm" variant="outline" onClick={() => onDepositar(cheque)}>
          <Landmark aria-hidden="true" />
          Depositar
        </Button>
      ) : null}
      {rechazable ? (
        <Button type="button" size="sm" variant="ghost" onClick={() => onRechazar(cheque)}>
          <Ban aria-hidden="true" />
          Rechazar
        </Button>
      ) : null}
    </div>
  );
}

/**
 * La fecha y, debajo, cuanto falta. Vencido y por vencer son estados y van
 * como `Badge` (MASTER seccion 10: por vencer `warning`, vencido `destructive`);
 * lo demas es texto apagado.
 *
 * La cuenta regresiva es solo para los que todavia se van a cobrar (en cartera
 * o depositados). Un rechazado seguia diciendo «Faltan 5 días» al lado del
 * badge RECHAZADO; ahora dice por que se rechazo.
 */
function Vencimiento({
  cheque,
  urgencia,
  dias,
}: {
  cheque: ChequeCartera;
  urgencia: Urgencia;
  dias: number | null;
}): JSX.Element {
  const fecha = formatearFecha(cheque.fecha_cobro) || "—";
  if (cheque.estado !== "EN_CARTERA" && cheque.estado !== "DEPOSITADO") {
    const motivo = cheque.estado === "RECHAZADO" ? motivoRechazo(cheque.observaciones) : null;
    return (
      <span className="inline-flex flex-col items-start gap-0.5">
        <span className="text-sm tabular-nums">{fecha}</span>
        {motivo ? (
          <span className="max-w-64 text-xs text-muted-foreground">Motivo: {motivo}</span>
        ) : null}
      </span>
    );
  }
  const texto = textoVencimiento(urgencia, dias);
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className="text-sm tabular-nums">{fecha}</span>
      {urgencia === "VENCIDO" ? (
        <Badge variant="destructive">
          <TriangleAlert aria-hidden="true" />
          {texto}
        </Badge>
      ) : urgencia === "POR_VENCER" ? (
        <Badge variant="warning">{texto}</Badge>
      ) : (
        <span className="text-xs text-muted-foreground">{texto}</span>
      )}
    </span>
  );
}
